import { create } from 'zustand';
import {
  addToCart,
  fetchCartItems,
  removeFromCartById,
  removeFromCartByProductId,
  requestPurchase,
} from '../services/cartService';
import { CartGroup } from '../types/models/cart';

interface CartState {
  cartItems: CartGroup[];
  totalCount: number;
  isLoading: boolean;
  error: Error | null;

  fetchCartItems: () => Promise<void>;
  addToCart: (productId: number) => Promise<void>;
  requestPurchase: (cartItemId: number, quantity: number, paymentType: string) => Promise<void>;
  removeFromCartByProductId: (productId: number) => Promise<void>;
  removeFromCartById: (cartId: number) => Promise<void>;
  deleteProduct: (date: string, productId: number) => void;
}

export const useCartStore = create<CartState>((set, get) => ({
  cartItems: [],
  totalCount: 0,
  isLoading: false,
  error: null,

  fetchCartItems: async () => {
    try {
      set({ isLoading: true, error: null });

      const response = await fetchCartItems();

      set({
        cartItems: response,
        totalCount: response.reduce(
          (acc, group) => acc + group.cartProducts.length,
          0,
        ),
        isLoading: false,
      });
    } catch (error) {
      set({ error: error as Error, isLoading: false });
    }
  },

  addToCart: async (productId: number) => {
    try {
      set({ isLoading: true, error: null });

      await addToCart(productId).then(() => {
        get().fetchCartItems();
      });
    } catch (error) {
      set({ error: error as Error, isLoading: false });
    }
  },

  requestPurchase: async (cartItemId: number, quantity: number, paymentType: string) => {
    try {
      set({ isLoading: true, error: null });

      await requestPurchase(cartItemId, quantity, paymentType).then(() => {
        set({ isLoading: false });
      });
    } catch (error) {
      set({ error: error as Error, isLoading: false });
    }
  },

  removeFromCartByProductId: async (productId: number) => {
    try {
      set({ isLoading: true, error: null });

      await removeFromCartByProductId(productId).then(() => {
        get().fetchCartItems();
      });
    } catch (error) {
      set({ error: error as Error, isLoading: false });
    }
  },

  removeFromCartById: async (cartId: number) => {
    try {
      set({ isLoading: true, error: null });

      await removeFromCartById(cartId).then(() => {
        get().fetchCartItems();
      });
    } catch (error) {
      set({ error: error as Error, isLoading: false });
    }
  },

  deleteProduct: (date: string, productId: number) =>
    set((state) => {
      const newCartItems = [...state.cartItems];
      const dateIndex = newCartItems.findIndex((group) => group.date === date);

      if (dateIndex !== -1) {
        const productIndex = newCartItems[dateIndex].cartProducts.findIndex(
          (p) => p.id === productId,
        );
        if (productIndex !== -1) {
          newCartItems[dateIndex].cartProducts.splice(productIndex, 1);

          // 해당 날짜의 상품이 모두 삭제되었다면 그룹도 삭제
          if (newCartItems[dateIndex].cartProducts.length === 0) {
            newCartItems.splice(dateIndex, 1);
          }
        }
      }

      return { cartItems: newCartItems };
    }),
}));
